import React, { useState, useEffect, useRef } from "react";
import { FaLongArrowAltRight, FaRegEyeSlash } from "react-icons/fa";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { setTabData } from "../../redux/slices/tabSlice";
import { commonToast } from "../../helper/Toast";
import { useTranslation } from "react-i18next";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { ApiPost } from "../../helper/axiosFunctions";
import Loading from "../../components/Loading";
import { useNavigate } from "react-router-dom";
import LoginAttempts from "../../components/auth/LoginAttempts";
import { setProfileData } from "../../redux/slices/profileSlice";
import SchoolSelectionModal from "../../components/model/SchoolSelectionModal";
import {
  setSchoolIds,
  setShowSchoolModal,
  selectSchool,
  fetchSchoolsData,
} from "../../redux/slices/schoolSlice";
import { useSelector } from "react-redux";

const OTP_LENGTH = 4;
const RESEND_SECONDS = 59;
const ANONYMOUS_LOGIN_PASSWORD =
  process.env.REACT_APP_ANONYMOUS_LOGIN_PASSWORD || "";
const ANONYMOUS_TOKEN_BUFFER_MS = 15 * 1000; // refresh 15s before expiry

export default function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isShow, setIsShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isDisabled, setIsDisabled] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [loginResponse, setLoginResponse] = useState(null);
  const dispatch = useDispatch();

  // OTP / password mode (OTP is default)
  const [loginMode, setLoginMode] = useState("otp");
  const [otpSent, setOtpSent] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [resendTimer, setResendTimer] = useState(0);
  const otpInputRefs = useRef([]);
  const anonymousTokenRef = useRef({ accessToken: null, expiresAt: 0 });

  const { showSchoolModal } = useSelector((state) => state.schoolData);

  const MAX_ATTEMPTS = 3;
  const remainingAttempts = MAX_ATTEMPTS - failedAttempts;

  const LoginSchema = Yup.object().shape({
    username: Yup.string()
      .required(t("validation.userrequired"))
      .min(4, t("validation.usercharacter")),
    password: Yup.string().required(t("validation.passwordrequired")),
  });

  const OtpEmailSchema = Yup.object().shape({
    email: Yup.string()
      .required(t("validation.email_required"))
      .email(t("validation.email_invalid")),
  });

  useEffect(() => {
    let timer;
    if (isDisabled && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    } else if (timeLeft === 0 && isDisabled) {
      setIsDisabled(false);
      setFailedAttempts(0);
    }
    return () => clearInterval(timer);
  }, [isDisabled, timeLeft]);

  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const calculateTimeout = (attempts) => {
    if (attempts >= 3) {
      return 30 * (attempts - 2);
    }
    return 0;
  };

  /** Extract API error text from axios / API response shapes */
  const getApiErrorMessage = (error, fallback) => {
    return (
      error?.response?.data?.data?.error ||
      error?.response?.data?.data?.errorDescription ||
      error?.response?.data?.errorDescription ||
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.data?.data?.error ||
      error?.data?.errorDescription ||
      error?.data?.error ||
      error?.data?.message ||
      error?.errorDescription ||
      error?.message ||
      fallback
    );
  };

  /** Anonymous login → short-lived Bearer token for OTP request/verify */
  const getAnonymousAccessToken = async (forceRefresh = false) => {
    const cached = anonymousTokenRef.current;
    const stillValid =
      cached.accessToken &&
      cached.expiresAt - ANONYMOUS_TOKEN_BUFFER_MS > Date.now();

    if (!forceRefresh && stillValid) {
      return cached.accessToken;
    }

    const response = await ApiPost(
      "/auth/login/anonymous",
      { password: ANONYMOUS_LOGIN_PASSWORD },
      {},
      false
    );

    const tokens = response?.data?.tokens;
    const accessToken = tokens?.accessToken;
    if (!accessToken) {
      throw new Error(t("otpRequestFailed"));
    }

    const expiresInSec = Number(tokens?.expiresIn) || 120;
    anonymousTokenRef.current = {
      accessToken,
      expiresAt: Date.now() + expiresInSec * 1000,
    };
    return accessToken;
  };

  const registerFailedAttempt = (errorMessage) => {
    const newAttempts = failedAttempts + 1;
    setFailedAttempts(newAttempts);
    commonToast(errorMessage || t("loginFailed"), "error");

    if (newAttempts >= 3) {
      const timeoutDuration = calculateTimeout(newAttempts);
      setIsDisabled(true);
      setTimeLeft(timeoutDuration);
      commonToast(t("loginAttemptsExceeded"), "error");
    } else {
      commonToast(
        t("remainingAttempts", { count: remainingAttempts - 1 }),
        "warning"
      );
    }
  };

  /** Shared post-auth handling for password login and OTP verify */
  const handleAuthSuccess = (response) => {
    const Data = response?.data;
    if (!Data?.tokens) {
      return false;
    }

    setLoginResponse(response);
    const userRoles = Data?.userInfo?.userRoles || [];

    const restrictedRoles = ["zubrus_student", "zubrus_user"];
    const hasOnlyRestrictedRoles =
      userRoles.length > 0 &&
      userRoles.every((role) => restrictedRoles.includes(role));

    if (hasOnlyRestrictedRoles) {
      localStorage.removeItem("zubrus_user_data");
      commonToast(
        "Students are not allowed to log in to this platform.",
        "error"
      );
      return true;
    }

    const schoolRequiredRoles = ["zubrus_teacher"];
    const hasSchoolRequiredRole = userRoles.some((role) =>
      schoolRequiredRoles.includes(role)
    );

    if (!hasSchoolRequiredRole) {
      proceedWithLogin(response, null);
      setFailedAttempts(0);
      return true;
    }

    const userSchools =
      Data?.userInfo?.roleData?.zubrus_teacher?.schoolIds ||
      (Data?.userInfo?.roleData?.zubrus_student?.schoolId
        ? [Data?.userInfo?.roleData?.zubrus_student?.schoolId]
        : []) ||
      Data?.userInfo?.schoolIds ||
      Data?.userInfo?.schools ||
      Data?.schoolIds ||
      Data?.schools ||
      [];

    dispatch(setSchoolIds(userSchools));
    dispatch(setShowSchoolModal(true));

    if (userSchools.length > 0) {
      dispatch(
        fetchSchoolsData({
          schoolIds: userSchools,
          accessToken: Data?.tokens?.accessToken,
        })
      );
    }

    setFailedAttempts(0);
    return true;
  };

  const handleLogin = async (values) => {
    if (isDisabled) {
      commonToast(t("loginTimeout"), "error");
      return;
    }

    setLoading(true);
    try {
      // Password login is a public endpoint — no anonymous token needed.
      // (Do not send X-Version: v3; that causes 403. Handled in axios interceptor.)
      const response = await ApiPost("/auth/login", values, {}, false);
      const Data = response?.data;
      setLoading(false);

      if (Data?.tokens) {
        handleAuthSuccess(response);
      } else {
        const errorMessage =
          Data?.errorDescription ||
          Data?.error ||
          Data?.message ||
          response?.data?.errorDescription ||
          response?.message ||
          t("loginFailed");
        registerFailedAttempt(errorMessage);
      }
    } catch (error) {
      setLoading(false);
      registerFailedAttempt(getApiErrorMessage(error, t("loginFailed")));
    }
  };

  const handleRequestOtp = async (values) => {
    setLoading(true);
    try {
      const accessToken = await getAnonymousAccessToken(true);
      await ApiPost(
        "/auth/otp/request",
        { email: values.email },
        {},
        false,
        accessToken
      );
      setOtpEmail(values.email);
      setOtpSent(true);
      setOtp(Array(OTP_LENGTH).fill(""));
      setResendTimer(RESEND_SECONDS);
      commonToast(t("otpSentSuccess"), "success");
      setTimeout(() => otpInputRefs.current[0]?.focus(), 50);
    } catch (error) {
      commonToast(getApiErrorMessage(error, t("otpRequestFailed")), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (otpCode) => {
    if (!otpEmail || otpCode.length !== OTP_LENGTH) return;

    setLoading(true);
    try {
      const accessToken = await getAnonymousAccessToken();
      const response = await ApiPost(
        "/auth/otp/verify",
        { email: otpEmail, otpCode },
        {},
        false,
        accessToken
      );
      setLoading(false);

      const Data = response?.data;
      if (Data?.tokens) {
        anonymousTokenRef.current = { accessToken: null, expiresAt: 0 };
        handleAuthSuccess(response);
      } else {
        const errorMessage =
          Data?.error ||
          Data?.errorDescription ||
          Data?.message ||
          response?.data?.error ||
          response?.message ||
          t("otpVerifyFailed");
        commonToast(errorMessage, "error");
        setOtp(Array(OTP_LENGTH).fill(""));
        otpInputRefs.current[0]?.focus();
      }
    } catch (error) {
      commonToast(getApiErrorMessage(error, t("otpVerifyFailed")), "error");
      setLoading(false);
      setOtp(Array(OTP_LENGTH).fill(""));
      otpInputRefs.current[0]?.focus();
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0 || !otpEmail || loading) return;
    setLoading(true);
    try {
      const accessToken = await getAnonymousAccessToken(true);
      await ApiPost(
        "/auth/otp/request",
        { email: otpEmail },
        {},
        false,
        accessToken
      );
      setOtp(Array(OTP_LENGTH).fill(""));
      setResendTimer(RESEND_SECONDS);
      commonToast(t("otpSentSuccess"), "success");
      otpInputRefs.current[0]?.focus();
    } catch (error) {
      commonToast(getApiErrorMessage(error, t("otpRequestFailed")), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpDigitChange = (index, e) => {
    const value = e.target.value;
    if (value.match(/^[0-9]$/)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (index < OTP_LENGTH - 1) {
        otpInputRefs.current[index + 1]?.focus();
      }

      if (newOtp.every((d) => d !== "") && newOtp.join("").length === OTP_LENGTH) {
        handleVerifyOtp(newOtp.join(""));
      }
    } else if (value === "") {
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const newOtp = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((digit, i) => {
      newOtp[i] = digit;
    });
    setOtp(newOtp);
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    otpInputRefs.current[focusIndex]?.focus();
    if (pasted.length === OTP_LENGTH) {
      handleVerifyOtp(pasted);
    }
  };

  const switchToPassword = () => {
    setLoginMode("password");
    setOtpSent(false);
    setOtp(Array(OTP_LENGTH).fill(""));
    setResendTimer(0);
    anonymousTokenRef.current = { accessToken: null, expiresAt: 0 };
  };

  const switchToOtp = () => {
    setLoginMode("otp");
    setOtpSent(false);
    setOtp(Array(OTP_LENGTH).fill(""));
    setResendTimer(0);
    anonymousTokenRef.current = { accessToken: null, expiresAt: 0 };
  };

  const proceedWithLogin = (response, selectedSchoolId) => {
    const userRoles = response?.data?.userInfo?.userRoles || [];

    const userData = {
      ...response,
      selectedSchoolId: selectedSchoolId,
    };

    localStorage.setItem("zubrus_user_data", JSON.stringify(userData));
    dispatch(setProfileData(response?.data?.userInfo));
    localStorage.removeItem("zubrus_admin_data");

    if (userRoles.includes("zubrus_admin")) {
      dispatch(setTabData("classes"));
    } else if (userRoles.includes("zubrus_content_creator")) {
      dispatch(setTabData("workshops"));
    } else if (userRoles.includes("zubrus_parent")) {
      dispatch(setTabData("child"));
    } else {
      dispatch(setTabData("classes"));
    }

    commonToast(t("loginmsg"), "success");

    if (userRoles.length === 1 && userRoles[0] === "zubrus_admin") {
      navigate("/admin/userlist");
    } else if (userRoles.includes("zubrus_parent")) {
      navigate("/mychildren");
    } else {
      navigate("/classes");
    }
  };

  const handleSchoolSelect = (selectedSchool) => {
    const updatedResponse = {
      ...loginResponse,
      selectedSchoolId: selectedSchool?.id || null,
      selectedSchool: selectedSchool,
    };

    dispatch(selectSchool(selectedSchool));
    proceedWithLogin(updatedResponse, selectedSchool?.id || null);
  };

  return (
    <div>
      <div className="bg-white max-w-4xl flex md:flex-col justify-start items-center gap-10 mx-auto my-6 rounded-[32px] p-5">
        <div className="w-1/2 md:w-full md:hidden">
          <img
            src="/assets/images/loginposterzubrus.png"
            alt="Login Poster"
            className="w-full h-[536px] "
          />
        </div>
        <div className="w-1/2 md:w-full">
          <h3 className="subheading-text mb-6"> {t("login")}</h3>

          {loginMode === "password" && (
            <LoginAttempts
              failedAttempts={failedAttempts}
              isDisabled={isDisabled}
              timeLeft={timeLeft}
              remainingAttempts={remainingAttempts}
              maxAttempts={MAX_ATTEMPTS}
            />
          )}

          {/* ── OTP mode (default) ── */}
          {loginMode === "otp" && !otpSent && (
            <Formik
              initialValues={{ email: otpEmail || "" }}
              validationSchema={OtpEmailSchema}
              onSubmit={(values) => handleRequestOtp(values)}
            >
              {({ errors, touched }) => (
                <Form>
                  <div>
                    <Field
                      name="email"
                      type="email"
                      placeholder={t("email")}
                      className={`regular-text border h-[56px] w-full rounded-xl ps-5 shadow-inner outline-none placeholder:text-lightgray text-black ${
                        errors.email && touched.email ? "border-red-500" : ""
                      }`}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="medium-text primary-btn w-full mt-6 mx-auto"
                  >
                    {loading ? <Loading /> : t("sendOtp")}
                  </button>
                </Form>
              )}
            </Formik>
          )}

          {loginMode === "otp" && otpSent && (
            <div>
              <p className="regular-text text-lightgray mb-2">
                {t("opth1")}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                {otpEmail}
              </p>

              <div className="flex justify-center gap-2 my-4" onPaste={handleOtpPaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    disabled={loading}
                    onChange={(e) => handleOtpDigitChange(index, e)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="w-12 h-12 text-center text-xl border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100"
                  />
                ))}
              </div>

              <button
                type="button"
                disabled={loading || otp.join("").length !== OTP_LENGTH}
                onClick={() => handleVerifyOtp(otp.join(""))}
                className={`medium-text primary-btn w-full mt-4 mx-auto ${
                  otp.join("").length !== OTP_LENGTH
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {loading ? <Loading /> : t("verifyOtp")}
              </button>

              <div className="mt-4 text-center">
                {resendTimer > 0 ? (
                  <p className="text-gray-400 text-sm">
                    {t("otpcode")} ({resendTimer} {t("seconds")})
                  </p>
                ) : (
                  <button
                    type="button"
                    className="text-darkblue text-sm hover:underline"
                    disabled={loading}
                    onClick={handleResendOtp}
                  >
                    {t("otpcode")}
                  </button>
                )}
              </div>

              <button
                type="button"
                className="regular-text text-darkblue mt-4 underline"
                onClick={() => {
                  setOtpSent(false);
                  setOtp(Array(OTP_LENGTH).fill(""));
                }}
              >
                {t("changeEmail")}
              </button>
            </div>
          )}

          {/* ── Password mode ── */}
          {loginMode === "password" && (
            <Formik
              initialValues={{
                username: "",
                password: "",
              }}
              validationSchema={LoginSchema}
              onSubmit={(values) => {
                handleLogin(values);
              }}
            >
              {({ errors, touched }) => (
                <Form>
                  <div>
                    <Field
                      name="username"
                      type="text"
                      placeholder={`${t("user")} ${t("name")}`}
                      disabled={isDisabled}
                      className={`regular-text border h-[56px] w-full rounded-xl ps-5 shadow-inner outline-none placeholder:text-lightgray text-black ${
                        errors.username && touched.username
                          ? "border-red-500"
                          : ""
                      } ${isDisabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
                    />
                    <ErrorMessage
                      name="username"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  <div className="relative flex items-center mt-6">
                    <Field
                      name="password"
                      type={`${isShow ? "text" : "password"}`}
                      placeholder={t("password")}
                      disabled={isDisabled}
                      className={`regular-text border h-[56px] w-full rounded-xl ps-5 shadow-inner outline-none placeholder:text-lightgray text-black ${
                        errors.password && touched.password
                          ? "border-red-500"
                          : ""
                      } ${isDisabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
                    />
                    {isShow ? (
                      <MdOutlineRemoveRedEye
                        className="absolute cursor-pointer right-5 text-xl"
                        onClick={() => !isDisabled && setIsShow(!isShow)}
                      />
                    ) : (
                      <FaRegEyeSlash
                        className="absolute cursor-pointer right-5 text-xl"
                        onClick={() => !isDisabled && setIsShow(!isShow)}
                      />
                    )}
                  </div>
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />

                  <button
                    type="submit"
                    disabled={loading || isDisabled}
                    className={`medium-text primary-btn w-full mt-6 mx-auto ${
                      isDisabled ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {loading ? <Loading /> : t("login")}
                  </button>
                </Form>
              )}
            </Formik>
          )}

          <div className="mt-4 text-center">
            {loginMode === "otp" ? (
              <button
                type="button"
                className="regular-text text-darkblue underline"
                onClick={switchToPassword}
              >
                {t("loginWithPassword")}
              </button>
            ) : (
              <button
                type="button"
                className="regular-text text-darkblue underline"
                onClick={switchToOtp}
              >
                {t("loginWithOtp")}
              </button>
            )}
          </div>

          <hr className="my-6" />

          <div className="flex justify-between items-center ">
            <span className="regular-text">{t("firstTime")}</span>
            <Link
              to={`/auth/signup`}
              className="regular-text text-darkblue cursor-pointer flex items-center gap-2"
            >
              {t("signup")} <FaLongArrowAltRight />
            </Link>
          </div>
        </div>
      </div>

      <SchoolSelectionModal
        isOpen={showSchoolModal}
        onClose={() => dispatch(setShowSchoolModal(false))}
        onSchoolSelect={handleSchoolSelect}
      />
    </div>
  );
}
